import { motion } from 'framer-motion';
import { Lightbulb, Target, Zap } from 'lucide-react';

const FounderJourneyPage = () => {
  const milestones = [
    {
      year: '2018',
      title: 'The Beginning',
      desc: 'Founded Appdroid Tech with a vision to transform digital experiences.',
    },
    {
      year: '2019',
      title: 'First Major Client',
      desc: 'Delivered enterprise solutions for Fortune 500 companies.',
    },
    {
      year: '2021',
      title: 'Team Expansion',
      desc: 'Grew from 5 to 25+ talented professionals.',
    },
    {
      year: '2023',
      title: 'Fusion Scope Launch',
      desc: 'Introduced AI-powered cinematic content vertical.',
    },
    {
      year: '2024',
      title: 'Industry Recognition',
      desc: 'Awarded Best AI Content Studio in India.',
    },
    {
      year: '2025',
      title: 'Global Expansion',
      desc: 'Serving clients across 15+ countries.',
    },
  ];

  const beliefs = [
    {
      icon: <Lightbulb />,
      title: 'AI should make brands feel more human',
      desc: 'Technology is a tool for connection, not replacement.',
    },
    {
      icon: <Target />,
      title: "Great design isn't decoration. It's direction",
      desc: 'Every pixel serves a purpose. Every frame tells a story.',
    },
    {
      icon: <Zap />,
      title: "We don't follow trends. We set the standard",
      desc: 'Innovation over imitation. Excellence over expectation.',
    },
  ];

  return (
    <div data-testid="journey-page" className="min-h-screen">
      {/* Hero */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-6xl md:text-8xl font-black tracking-tighter leading-none uppercase mb-6">
                The Journey
              </h1>
              <p className="text-xl md:text-2xl text-gray-400 mb-8 leading-relaxed">
                "Building the future, one innovation at a time. Our story is just beginning."
              </p>
              <div className="border-l-4 border-purple-500 pl-6">
                <p className="text-lg font-bold mb-1">Amit Bhamere</p>
                <p className="text-sm text-purple-500">Founder & CEO</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-purple-500/20 to-blue-500/20 border border-white/10">
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-9xl font-black text-white/5">AT</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 md:py-32 bg-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-extrabold text-primary tracking-tight leading-tight mb-4">
              Our Story
            </h2>
            <p className="text-lg text-gray-400">Every milestone tells a story.</p>
          </motion.div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-0 md:left-1/2 top-0 bottom-0 w-0.5 bg-purple-500/30" />

            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative mb-12 md:mb-16 ${
                  index % 2 === 0 ? 'md:pr-[calc(50%+2rem)] md:text-right' : 'md:pl-[calc(50%+2rem)]'
                }`}
              >
                {/* Timeline Dot */}
                <div className="absolute left-0 md:left-1/2 top-0 w-4 h-4 bg-purple-500 rounded-full -translate-x-[7px] md:-translate-x-1/2" />

                <div className="ml-8 md:ml-0">
                  <div className="text-xs font-bold tracking-[0.2em] uppercase text-purple-500 mb-2">
                    {milestone.year}
                  </div>
                  <h3 className="text-2xl font-bold mb-2">{milestone.title}</h3>
                  <p className="text-gray-400">{milestone.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-extrabold text-primary tracking-tight leading-tight mb-4">
              What We Believe
            </h2>
            <p className="text-lg text-gray-400">Core principles that guide us.</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {beliefs.map((belief, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-500/20 rounded-full text-purple-500 mb-6">
                  {belief.icon}
                </div>
                <h3 className="text-2xl font-bold mb-4 leading-tight">{belief.title}</h3>
                <p className="text-gray-400 leading-relaxed">{belief.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default FounderJourneyPage;